<script setup>
import { computed, ref, onMounted, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { RouterLink } from 'vue-router';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

import Table from 'dashboard/components/table/Table.vue';
import CustomPagination from 'dashboard/components/table/CustomPagination.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';

const props = defineProps({
  filterMessageStatusOptions: { type: Array, default: () => [] },
  metric: { type: Object, default: () => ({}) },
  conversationDataFilterOptions: { type: Object, default: () => ({}) },
  totalResponseCount: { type: Number, default: 0 },
});

const emit = defineEmits(['closeConversationViewModal']);

const { t } = useI18n();
const store = useStore();
const pageIndex = ref(1);

const ALLOWED_METRIC_KEYS_FOR_CONVERSATIONS_VIEW = {
  conversations_count: 'conversations_view_data',
};

const conversationViewDataMetric = computed(() => {
  return ALLOWED_METRIC_KEYS_FOR_CONVERSATIONS_VIEW[props.metric?.KEY] || '';
});

const accountReport = computed(() => store.getters.getAccountReports);

const conversations = computed(() => {
  const metricKey = conversationViewDataMetric.value;
  const data = accountReport.value.data?.[metricKey];
  return Array.isArray(data) ? data : [];
});

const tableData = computed(() =>
  conversations.value.map(response => ({
    accountId: response.account_id,
    contact: response.contact,
    display_id: response.display_id,
    protocol: response.protocol,
    status: response.status,
  }))
);

const defaultSpanRender = cellProps =>
  h(
    'span',

    {
      class: cellProps.getValue()
        ? 'capitalize text-n-slate-12'
        : 'capitalize text-n-slate-11',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('contact', {
    header: t('REPORT.CONVERSATIONS_VIEW.CONTACT'),
    width: 200,
    cell: cellProps => {
      const contact = cellProps.getValue();
      return contact
        ? h(UserAvatarWithName, {
            user: contact,
            size: '24px',
            textClass: 'text-sm text-n-slate-12 max-w-[200px]',
          })
        : h('span', {}, '---');
    },
  }),
  columnHelper.accessor('status', {
    header: t('REPORT.CONVERSATIONS_VIEW.STATUS.HEADER'),
    width: 200,
    cell: cellProps => {
      const status = cellProps.getValue();
      return defaultSpanRender({
        ...cellProps,
        getValue: () =>
          t(`REPORT.CONVERSATIONS_VIEW.STATUS.${status?.toUpperCase()}`) ||
          '---',
      });
    },
  }),
  columnHelper.accessor('protocol', {
    header: t('CONVERSATION.HEADER.PROTOCOL_LABEL'),
    width: 200,
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('display_id', {
    header: t('REPORT.CONVERSATIONS_VIEW.ACTIONS'),
    width: 200,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(
        RouterLink,
        {
          to: frontendURL(
            conversationUrl({ accountId: row.accountId, id: row.display_id })
          ),
          class: 'text-blue-800 hover:underline',
        },
        () => t('REPORT.CONVERSATIONS_VIEW.ACCESS_CHAT')
      );
    },
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

const fetchConversationsViewData = () => {
  try {
    const { from, to, groupBy, businessHours, type, id } =
      props.conversationDataFilterOptions;
    store.dispatch('fetchAccountReport', {
      metric: conversationViewDataMetric.value,
      from,
      to,
      id,
      type,
      groupBy,
      businessHours,
      page: pageIndex.value,
    });
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const onPageChange = newVal => {
  if (pageIndex.value !== newVal) {
    pageIndex.value = newVal;
    fetchConversationsViewData();
  }
};

const closeModal = () => {
  emit('closeConversationViewModal');
};

onMounted(() => {
  fetchConversationsViewData();
});
</script>

<template>
  <div class="table-container">
    <woot-modal-header
      :header-title="metric.NAME"
      :header-content="$t('REPORT.CONVERSATIONS_VIEW.MODAL_DESCRIPTION')"
      class="mb-4"
    />
    <div
      class="flex flex-col justify-center content-box pb-4 px-2 w-[90%] mx-auto"
    >
      <div class="main-container">
        <div class="w-full flex gap-4 flex-col">
          <div>
            <woot-loading-state
              v-if="accountReport.isFetching[conversationViewDataMetric]"
              class="text-xs"
              :message="$t('REPORT.CONVERSATIONS_VIEW.LOADING')"
            />
            <section v-else>
              <Table :table="table" />
              <CustomPagination
                class="mt-2"
                :total="totalResponseCount"
                :current-page="pageIndex"
                @page-change="onPageChange"
              />
              <EmptyState
                v-if="!tableData.length"
                :title="$t('REPORT.CONVERSATIONS_VIEW.NO_ENOUGH_DATA')"
              />
            </section>
          </div>

          <div class="flex flex-row justify-end gap-2 pb-1 w-full">
            <woot-button variant="clear" @click="closeModal">
              {{ $t('REPORT.CONVERSATIONS_VIEW.CLOSE_BUTTON') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
